import PizZip from 'pizzip';
import * as XLSX from 'xlsx';

export const PHISHING_CAMPAIGN_REPORT_TEMPLATE_URL = '/templates/gabarit-rapport-campagne-hameconnage-2025.docx';
export const PHISHING_RESULTS_CHART_TEMPLATE_URL = '/templates/gabarit-graphique-resultats-hameconnage-v20251.xlsx';

export interface UsecurePhishingCampaignStats {
  targetedUsers?: number;
  emailsSent?: number;
  emailsOpened?: number;
  clicks?: number;
  credentialSubmissions?: number;
  reports?: number;
  riskyUsers: Array<{ name: string; email?: string; action: string }>;
  detectedColumns: string[];
  sheetName?: string;
  source?: 'api' | 'excel';
}

export interface UsecurePhishingCampaignReportInput {
  clientName: string;
  campaignName: string;
  startDate: string;
  endDate: string;
  landingPageUrl?: string;
  emailScenario?: string;
  senderName?: string;
  senderEmail?: string;
  emailSubject?: string;
  templateFileName?: string;
  emailScreenshotName?: string;
  emailScreenshotDataUrl?: string;
  emailContainsQrCode?: boolean;
  excelFileName?: string;
  advisorNotes?: string;
  recommendations?: string;
  followupNote?: string;
  projectTitle?: string;
  taskTitle?: string;
  generatedBy?: string;
  stats: UsecurePhishingCampaignStats;
}

export interface StoredUsecurePhishingCampaignReport extends UsecurePhishingCampaignReportInput {
  id: string;
  status: 'generated' | 'draft';
  generatedAt: string;
  docxFileName: string;
  projectDeliverableId?: number | null;
  excelSourceName?: string;
  clickRate?: number | null;
  submissionRate?: number | null;
}

const metricAliases: Record<keyof Omit<UsecurePhishingCampaignStats, 'riskyUsers' | 'detectedColumns' | 'sheetName' | 'source'>, string[]> = {
  targetedUsers: ['targeted', 'target', 'recipients', 'destinataires', 'utilisateurs cibles', 'users', 'learners', 'envoyé à'],
  emailsSent: ['sent', 'envoyés', 'envoyes', 'emails sent', 'courriels envoyés', 'courriels envoyes'],
  emailsOpened: ['opened', 'opens', 'ouvert', 'courriels ouverts', 'emails opened'],
  clicks: ['clicked', 'clicks', 'clics', 'link clicked', 'visited', 'visits', 'visité', 'visites'],
  credentialSubmissions: ['submitted', 'submission', 'compromised', 'compromise', 'credentials', 'identifiants', 'soumission'],
  reports: ['reported', 'reports', 'signalements', 'signalé', 'reported email'],
};

function normalize(value: unknown) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9@.]+/g, ' ')
    .trim();
}

function toNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const match = String(value || '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function columnIndex(headers: string[], aliases: string[]) {
  return headers.findIndex((header) => aliases.some((alias) => header.includes(normalize(alias))));
}

function rowHasSignal(row: unknown[], headers: string[], aliases: string[]) {
  const idx = columnIndex(headers, aliases);
  if (idx === -1) return false;
  const value = row[idx];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  const normalized = normalize(value);
  return Boolean(normalized && !['no', 'non', 'false', '0', 'n a', 'na', 'aucun'].includes(normalized));
}

function metricFromRows(rows: unknown[][], headers: string[], key: keyof typeof metricAliases) {
  const idx = columnIndex(headers, metricAliases[key]);
  if (idx === -1) return undefined;
  const values = rows.map((row) => row[idx]).filter((value) => value !== undefined && value !== null && String(value).trim() !== '');
  if (!values.length) return undefined;
  const numericValues = values.map(toNumber).filter((value) => value > 0);
  if (!numericValues.length) return undefined;
  const uniqueNumeric = new Set(numericValues.map((value) => String(value)));
  if (uniqueNumeric.size === 1 && numericValues[0] > values.length) return numericValues[0];
  return numericValues.length;
}

export function parseUsecureCampaignWorkbook(buffer: ArrayBuffer): UsecurePhishingCampaignStats {
  // Ne pas inventer les métriques absentes: seules les colonnes réellement détectées alimentent les compteurs.
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!sheet) {
    return { riskyUsers: [], detectedColumns: [], sheetName };
  }
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false });
  const headerRowIndex = rawRows.findIndex((row) => Array.isArray(row) && row.filter(Boolean).length >= 2);
  const headerRow = headerRowIndex >= 0 ? rawRows[headerRowIndex] : [];
  const headers = headerRow.map(normalize);
  const rows = rawRows.slice(headerRowIndex + 1).filter((row) => Array.isArray(row) && row.some((cell) => String(cell || '').trim()));
  const emailIdx = columnIndex(headers, ['email', 'courriel', 'mail']);
  const nameIdx = columnIndex(headers, ['name', 'nom', 'learner', 'user', 'utilisateur']);
  const riskyUsers = rows
    .filter((row) => rowHasSignal(row, headers, metricAliases.clicks) || rowHasSignal(row, headers, metricAliases.credentialSubmissions))
    .slice(0, 100)
    .map((row) => ({
      name: String(nameIdx >= 0 ? row[nameIdx] : '').trim() || String(emailIdx >= 0 ? row[emailIdx] : '').trim() || 'Utilisateur à risque',
      email: emailIdx >= 0 ? String(row[emailIdx] || '').trim() || undefined : undefined,
      action: rowHasSignal(row, headers, metricAliases.credentialSubmissions) ? 'Soumission d’identifiants' : 'Clic',
    }));
  return {
    targetedUsers: metricFromRows(rows, headers, 'targetedUsers') || (rows.length || undefined),
    emailsSent: metricFromRows(rows, headers, 'emailsSent'),
    emailsOpened: metricFromRows(rows, headers, 'emailsOpened'),
    clicks: metricFromRows(rows, headers, 'clicks'),
    credentialSubmissions: metricFromRows(rows, headers, 'credentialSubmissions'),
    reports: metricFromRows(rows, headers, 'reports'),
    riskyUsers,
    detectedColumns: headerRow.map((value) => String(value || '').trim()).filter(Boolean),
    sheetName,
  };
}

export function calculatePhishingReportRates(stats: UsecurePhishingCampaignStats) {
  const denominator = stats.targetedUsers || stats.emailsSent || 0;
  return {
    clickRate: denominator && stats.clicks !== undefined ? Number(((stats.clicks / denominator) * 100).toFixed(1)) : null,
    submissionRate: denominator && stats.credentialSubmissions !== undefined ? Number(((stats.credentialSubmissions / denominator) * 100).toFixed(1)) : null,
  };
}

export function buildUsecurePhishingReportFileName(clientName: string, campaignName: string) {
  const clean = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9-_ ]+/g, '').trim().replace(/\s+/g, '_') || 'rapport';
  return `${clean(clientName)}_${clean(campaignName)}_rapport_hameconnage.docx`;
}

function escapeXml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}


function escapeXmlAttribute(value: unknown) {
  return escapeXml(value).replace(/`/g, '&#96;');
}

function decodeDataUrl(dataUrl?: string) {
  const match = String(dataUrl || '').match(/^data:([^;,]+);base64,(.+)$/);
  if (!match) return null;
  const mimeType = match[1];
  const base64 = match[2];
  const extension = mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpg' : mimeType.includes('png') ? 'png' : mimeType.includes('gif') ? 'gif' : 'png';
  return { mimeType, base64, extension };
}

function ensureContentTypeDefault(xml: string, extension: string, contentType: string) {
  if (new RegExp(`<Default\\s+Extension=\"${extension}\"`, 'i').test(xml)) return xml;
  return xml.replace('</Types>', `<Default Extension="${extension}" ContentType="${contentType}"/></Types>`);
}

function nextRelationshipId(relsXml: string) {
  const ids = [...relsXml.matchAll(/Id="rId(\d+)"/g)].map((match) => Number(match[1])).filter(Number.isFinite);
  return `rId${Math.max(0, ...ids) + 1}`;
}

function imageDrawingParagraph(relId: string, fileName: string, options: { docPrId?: number; name?: string; description?: string; cx?: number; cy?: number } = {}) {
  const name = escapeXmlAttribute(options.name || fileName || 'Image du rapport');
  const description = escapeXmlAttribute(options.description || name);
  const docPrId = options.docPrId || 91001;
  const cNvPrId = docPrId + 1;
  const cx = options.cx || 5486400;
  const cy = options.cy || 3086100;
  return `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${cx}" cy="${cy}"/><wp:effectExtent l="0" t="0" r="0" b="0"/><wp:docPr id="${docPrId}" name="${name}" descr="${description}"/><wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/></wp:cNvGraphicFramePr><a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:nvPicPr><pic:cNvPr id="${cNvPrId}" name="${name}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="${relId}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`;
}

function valueOrDash(value?: string | number | null) {
  if (value === undefined || value === null || String(value).trim() === '') return '—';
  return String(value);
}

function formatMetric(value?: number | null, suffix = '') {
  if (value === undefined || value === null) return 'Non disponible';
  return `${value.toLocaleString('fr-CA')}${suffix}`;
}

function percent(value: number) {
  return `${Math.round(value * 1000) / 10} %`;
}

function phishingChartMetrics(input: UsecurePhishingCampaignReportInput) {
  const sent = Math.max(1, input.stats.emailsSent || input.stats.targetedUsers || 0);
  const submitted = Math.max(0, input.stats.credentialSubmissions || 0);
  const clickedOnly = Math.max(0, (input.stats.clicks || 0) - submitted);
  const readOrIgnored = Math.max(0, sent - clickedOnly - submitted);
  return [
    { label: 'Uniquement lu ou ignoré le courriel', client: readOrIgnored / sent, average: 0.7825 },
    { label: 'Cliqué sur le lien', client: clickedOnly / sent, average: 0.1532 },
    { label: 'Rempli le formulaire ou téléchargé le document', client: submitted / sent, average: 0.0647 },
  ];
}

export function buildPhishingResultsChartSvg(input: UsecurePhishingCampaignReportInput) {
  const metrics = phishingChartMetrics(input);
  const clientLabel = `${input.clientName || 'Client'} - ${formatMonthYear(input.endDate || input.startDate)}`;
  const width = 1280;
  const height = 720;
  const plotLeft = 470;
  const plotTop = 120;
  const rowGap = 150;
  const barHeight = 34;
  const plotWidth = 620;
  const axis = [0, 0.25, 0.5, 0.75, 1];
  const labelLines = [
    ['Uniquement lu ou', 'ignoré le courriel'],
    ['Cliqué sur le lien'],
    ['Rempli le formulaire', 'ou téléchargé le document'],
  ];
  const bar = (value: number, x: number, y: number, color: string) => `<rect x="${x}" y="${y}" width="${Math.max(2, value * plotWidth)}" height="${barHeight}" rx="6" fill="${color}"/>`;
  const text = (value: string, x: number, y: number, attrs = '') => `<text x="${x}" y="${y}" ${attrs}>${escapeXml(value)}</text>`;
  const multilineLabel = (lines: string[], x: number, y: number) => `<text x="${x}" y="${y}" font-family="Aptos, Arial, sans-serif" font-size="24" fill="#334155">${lines.map((line, index) => `<tspan x="${x}" dy="${index === 0 ? 0 : 30}">${escapeXml(line)}</tspan>`).join('')}</text>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="#ffffff"/>
  ${text('Résultats de la campagne vs clients BZ', width / 2, 54, 'text-anchor="middle" font-family="Aptos, Arial, sans-serif" font-size="32" font-weight="700" fill="#1f2937"')}
  ${axis.map((tick) => `<line x1="${plotLeft + tick * plotWidth}" y1="${plotTop - 34}" x2="${plotLeft + tick * plotWidth}" y2="${plotTop + rowGap * 2 + 108}" stroke="#e5e7eb" stroke-width="1"/>${text(`${tick * 100}%`, plotLeft + tick * plotWidth, plotTop + rowGap * 2 + 145, 'text-anchor="middle" font-family="Aptos, Arial, sans-serif" font-size="17" fill="#64748b"')}`).join('')}
  ${metrics.map((metric, index) => {
    const y = plotTop + index * rowGap;
    return `${multilineLabel(labelLines[index] || [metric.label], 42, y + 34)}
      ${bar(metric.average, plotLeft, y, '#94a3b8')}
      ${bar(metric.client, plotLeft, y + 50, '#2563eb')}
      ${text(percent(metric.average), Math.min(plotLeft + plotWidth - 6, plotLeft + Math.max(60, metric.average * plotWidth) + 16), y + 25, 'font-family="Aptos, Arial, sans-serif" font-size="18" fill="#475569"')}
      ${text(percent(metric.client), Math.min(plotLeft + plotWidth - 6, plotLeft + Math.max(60, metric.client * plotWidth) + 16), y + 75, 'font-family="Aptos, Arial, sans-serif" font-size="18" font-weight="700" fill="#1d4ed8"')}`;
  }).join('')}
  <rect x="${plotLeft}" y="${height - 84}" width="30" height="18" fill="#2563eb" rx="4"/>${text(clientLabel, plotLeft + 42, height - 68, 'font-family="Aptos, Arial, sans-serif" font-size="20" fill="#1f2937"')}
  <rect x="${plotLeft + 450}" y="${height - 84}" width="30" height="18" fill="#94a3b8" rx="4"/>${text('Moyenne des clients de BZ', plotLeft + 492, height - 68, 'font-family="Aptos, Arial, sans-serif" font-size="20" fill="#1f2937"')}
</svg>`;
}

function paragraphXml(text: string, style = '') {
  const styleXml = style ? `<w:pPr><w:pStyle w:val="${style}"/></w:pPr>` : '';
  return `<w:p>${styleXml}<w:r><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
}

function bulletXml(text: string) {
  return paragraphXml(`• ${text}`);
}

function tableXml(headers: string[], rows: string[][]) {
  const rowXml = (cells: string[], header = false) => `<w:tr>${cells.map((cell) => `<w:tc><w:tcPr><w:tcW w:w="2400" w:type="dxa"/></w:tcPr><w:p><w:r>${header ? '<w:rPr><w:b/></w:rPr>' : ''}<w:t xml:space="preserve">${escapeXml(cell)}</w:t></w:r></w:p></w:tc>`).join('')}</w:tr>`;
  return `<w:tbl><w:tblPr><w:tblW w:w="0" w:type="auto"/><w:tblBorders><w:top w:val="single" w:sz="4" w:space="0" w:color="B8C7D9"/><w:left w:val="single" w:sz="4" w:space="0" w:color="B8C7D9"/><w:bottom w:val="single" w:sz="4" w:space="0" w:color="B8C7D9"/><w:right w:val="single" w:sz="4" w:space="0" w:color="B8C7D9"/><w:insideH w:val="single" w:sz="4" w:space="0" w:color="DCE5F0"/><w:insideV w:val="single" w:sz="4" w:space="0" w:color="DCE5F0"/></w:tblBorders></w:tblPr>${rowXml(headers, true)}${rows.map((row) => rowXml(row)).join('')}</w:tbl>`;
}

function replaceInlineTextInParagraph(paragraphXml: string, searchText: string, value: string | number) {
  const textMatches = [...paragraphXml.matchAll(/<w:t(?=[\s>])([^>]*)>([\s\S]*?)<\/w:t>/g)];
  const plainText = textMatches.map((match) => match[2]).join('');
  const start = plainText.indexOf(searchText);
  if (start === -1) return paragraphXml;
  const end = start + searchText.length;
  let cursor = 0;
  let inserted = false;
  let next = paragraphXml;
  for (const match of textMatches) {
    const fullMatch = match[0];
    const attrs = match[1];
    const text = match[2];
    const textStart = cursor;
    const textEnd = cursor + text.length;
    cursor = textEnd;
    if (textEnd <= start || textStart >= end) continue;
    const before = textStart < start ? text.slice(0, start - textStart) : '';
    const after = textEnd > end ? text.slice(end - textStart) : '';
    const replacementText = inserted ? after : `${before}${escapeXml(value)}${after}`;
    inserted = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    next = next.replace(fullMatch, `<w:t${xmlSpace}>${replacementText}</w:t>`);
  }
  return next;
}

function replaceInlineTextAcrossRuns(xml: string, replacements: Record<string, string | number>) {
  let next = xml;
  for (const [searchText, value] of Object.entries(replacements)) {
    next = next.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => replaceInlineTextInParagraph(paragraph, searchText, value));
  }
  return next;
}

function replaceXmlElementText(xml: string, elementName: string, value: string | number) {
  const escaped = escapeXml(value);
  const elementPattern = new RegExp(`(<${elementName}(?:\\s[^>]*)?>)[\\s\\S]*?(<\\/${elementName}>)`);
  if (elementPattern.test(xml)) return xml.replace(elementPattern, `$1${escaped}$2`);
  return xml;
}

function formatMonthYear(value?: string) {
  const source = value ? new Date(value) : new Date();
  const date = Number.isNaN(source.getTime()) ? new Date() : source;
  return date.toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' });
}

function capitalizeFirstLetter(value: string) {
  return value ? `${value.charAt(0).toLocaleUpperCase('fr-CA')}${value.slice(1)}` : value;
}

function formatCoverMonthYear(value?: string) {
  return capitalizeFirstLetter(formatMonthYear(value));
}

function formatReportDate() {
  return new Date().toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function replacePhishingTemplateText(xml: string, input: UsecurePhishingCampaignReportInput) {
  const clientName = input.clientName || 'Client';
  const monthYear = formatMonthYear(input.endDate || input.startDate);
  const salutation = `Chers membres de l'équipe de ${clientName},`;
  const replacements = {
    'Cher / Chère [Nom du destinataire],': salutation,
    '[Nom du client]': clientName,
    '[Nom du destinataire]': `membres de l'équipe de ${clientName}`,
    '[Date]': formatReportDate(),
    '[Mois Année]': monthYear,
    'Mois Année': monthYear,
  };
  let next = xml;
  for (const [searchText, value] of Object.entries(replacements)) {
    next = next.split(searchText).join(escapeXml(value));
  }
  next = replaceInlineTextAcrossRuns(next, replacements);
  next = replaceInlineTextAcrossRuns(next, {
    [`Cher / Chère ${clientName},`]: salutation,
    [`Cher / Chère membres de l'équipe de ${clientName},`]: salutation,
  });
  return next;
}

export function fillPhishingDocumentPropertiesXml(xml: string, input: UsecurePhishingCampaignReportInput) {
  const clientName = input.clientName || 'Client';
  const monthYear = formatCoverMonthYear(input.endDate || input.startDate);
  let next = xml;
  next = replaceXmlElementText(next, 'dc:title', clientName);
  next = replaceXmlElementText(next, 'dc:subject', monthYear);
  next = replaceXmlElementText(next, 'Company', 'Rapport de campagne d’hameçonnage');
  next = replacePhishingTemplateText(next, input);
  return next;
}

export function findSectionParagraphStart(xml: string) {
  const sectionIndex = xml.lastIndexOf('<w:sectPr');
  if (sectionIndex === -1) return -1;
  const paragraphStart = xml.lastIndexOf('<w:p', sectionIndex);
  const previousParagraphEnd = xml.lastIndexOf('</w:p>', sectionIndex);
  return paragraphStart > previousParagraphEnd ? paragraphStart : sectionIndex;
}

export function appendPhishingReportDataToTemplate(xml: string, input: UsecurePhishingCampaignReportInput) {
  const { clickRate, submissionRate } = calculatePhishingReportRates(input.stats);
  const sourceLabel = input.stats.source === 'api' ? 'uSecure API/cache PRIsME' : input.excelFileName ? `Excel de secours — ${input.excelFileName}` : 'Non disponible';
  const riskyRows = input.stats.riskyUsers.length
    ? input.stats.riskyUsers.slice(0, 60).map((user) => [user.name, user.email || '—', user.action])
    : [['Aucun utilisateur à risque détecté', '—', '—']];
  const injected = [
    paragraphXml('Données de campagne remplies par PRIsME', 'Heading1'),
    paragraphXml(`Campagne : ${valueOrDash(input.campaignName)}`),
    paragraphXml(`Projet lié : ${valueOrDash(input.projectTitle)}`),
    paragraphXml(`Tâche liée : ${valueOrDash(input.taskTitle)}`),
    paragraphXml(`Date/Heure de début : ${valueOrDash(input.startDate)}`),
    paragraphXml(`Date/Heure de fin : ${valueOrDash(input.endDate)}`),
    paragraphXml(`Source des statistiques : ${sourceLabel}`),
    paragraphXml('Scénario et courriel simulé', 'Heading1'),
    paragraphXml(`URL de la page de destination : ${valueOrDash(input.landingPageUrl)}`),
    paragraphXml(`Prétexte / scénario : ${valueOrDash(input.emailScenario)}`),
    paragraphXml(`Nom de l’expéditeur : ${valueOrDash(input.senderName)}`),
    paragraphXml(`Adresse courriel de l’expéditeur : ${valueOrDash(input.senderEmail)}`),
    paragraphXml(`Sujet : ${valueOrDash(input.emailSubject)}`),
    paragraphXml(`Capture d’écran de l’exemple du courriel : ${valueOrDash(input.emailScreenshotName)}`),
    paragraphXml('Statistiques récupérées', 'Heading1'),
    tableXml(['Indicateur', 'Valeur'], [
      ['Nombre d’utilisateurs ciblés', formatMetric(input.stats.targetedUsers)],
      ['Courriels envoyés', formatMetric(input.stats.emailsSent)],
      ['Courriels ouverts', formatMetric(input.stats.emailsOpened)],
      ['Clics', formatMetric(input.stats.clicks)],
      ['Soumissions d’identifiants', formatMetric(input.stats.credentialSubmissions)],
      ['Signalements', formatMetric(input.stats.reports)],
      ['Taux de clic', formatMetric(clickRate, ' %')],
      ['Taux de soumission', formatMetric(submissionRate, ' %')],
    ]),
    paragraphXml('Utilisateurs à risque ou ayant cliqué', 'Heading1'),
    tableXml(['Nom', 'Courriel', 'Résultat'], riskyRows),
    paragraphXml('Constats et recommandations PRIsME', 'Heading1'),
    bulletXml(`Notes du conseiller : ${valueOrDash(input.advisorNotes)}`),
    bulletXml(`Recommandations / prochaines actions facultatives : ${valueOrDash(input.recommendations)}`),
    bulletXml(`Note de suivi : ${valueOrDash(input.followupNote)}`),
  ].join('');

  const sectionParagraphStart = findSectionParagraphStart(xml);
  if (sectionParagraphStart >= 0) {
    return `${xml.slice(0, sectionParagraphStart)}${injected}${xml.slice(sectionParagraphStart)}`;
  }
  return xml.replace('</w:body>', `${injected}</w:body>`);
}


function getParagraphPlainText(paragraphXml: string) {
  return paragraphXml.replace(/<w:t(?=[\s>])[^>]*>([\s\S]*?)<\/w:t>/g, '$1').replace(/<[^>]+>/g, '');
}

function replaceParagraphText(paragraphXml: string, value: string | number) {
  let replaced = false;
  return paragraphXml.replace(/<w:t(?=[\s>])([^>]*)>[\s\S]*?<\/w:t>/g, (_match, attrs) => {
    if (replaced) return `<w:t${attrs}></w:t>`;
    replaced = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    return `<w:t${xmlSpace}>${escapeXml(value)}</w:t>`;
  });
}

function replaceParagraphContaining(xml: string, containsText: string, value: string | number) {
  return xml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => getParagraphPlainText(paragraph).includes(containsText) ? replaceParagraphText(paragraph, value) : paragraph);
}

function replaceInFirstTable(xml: string, searchText: string, replacement: string) {
  const match = xml.match(/<w:tbl(?=[\s>])[\s\S]*?<\/w:tbl>/);
  if (!match || match.index === undefined) return xml;
  const tableXml = match[0].split(escapeXml(searchText)).join(escapeXml(replacement));
  return `${xml.slice(0, match.index)}${tableXml}${xml.slice(match.index + match[0].length)}`;
}

function pageBreakParagraph() {
  return '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';
}

function ensurePageBreakBeforeExactParagraph(xml: string, exactText: string) {
  return xml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => {
    if (getParagraphPlainText(paragraph).trim() !== exactText) return paragraph;
    if (paragraph.includes('w:type="page"') || paragraph.includes("w:type='page'")) return paragraph;
    return `${pageBreakParagraph()}${paragraph}`;
  });
}

function splitTableRows(tableXml: string) {
  return [...tableXml.matchAll(/<w:tr(?=[\s>])[\s\S]*?<\/w:tr>/g)].map((match) => match[0]);
}

function splitRowCells(rowXml: string) {
  return [...rowXml.matchAll(/<w:tc(?=[\s>])[\s\S]*?<\/w:tc>/g)].map((match) => match[0]);
}

function cloneStyledTableRow(rowXml: string) {
  return rowXml.replace(/w14:paraId="[^"]+"/g, '').replace(/w14:textId="[^"]+"/g, '').replace(/w:rsid(?:R|RPr|RDefault|P|Tr)="[^"]+"/g, '');
}

function replaceFirstTextInCell(cellXml: string, value: string | number) {
  const replacement = escapeXml(String(value ?? '').trim());
  let replaced = false;
  const next = cellXml.replace(/<w:t(?=[\s>])([^>]*)>[\s\S]*?<\/w:t>/g, (match, attrs) => {
    if (replaced) return `<w:t${attrs}></w:t>`;
    replaced = true;
    const xmlSpace = attrs.includes('xml:space=') ? attrs : `${attrs} xml:space="preserve"`;
    return `<w:t${xmlSpace}>${replacement}</w:t>`;
  });
  if (replaced) return next;
  const paragraph = `<w:p><w:r><w:t xml:space="preserve">${replacement}</w:t></w:r></w:p>`;
  if (/<w:p(?=[\s>])[\s\S]*?<\/w:p>/.test(next)) {
    return next.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/, paragraph);
  }
  return next.replace('</w:tc>', `${paragraph}</w:tc>`);
}

function replaceRowCellTexts(rowXml: string, values: Array<string | number>) {
  let next = cloneStyledTableRow(rowXml);
  const cells = splitRowCells(next);
  cells.forEach((cell, index) => {
    next = next.replace(cell, replaceFirstTextInCell(cell, values[index] ?? ''));
  });
  return next;
}

function replaceTableRowsByIndex(xml: string, tableIndex: number, rows: string[], transformTable?: (tableXml: string) => string) {
  const tables = [...xml.matchAll(/<w:tbl(?=[\s>])[\s\S]*?<\/w:tbl>/g)];
  const match = tables[tableIndex];
  if (!match) return xml;
  const tableXml = match[0];
  const rowMatches = [...tableXml.matchAll(/<w:tr(?=[\s>])[\s\S]*?<\/w:tr>/g)];
  if (!rowMatches.length) return xml;
  const firstRow = rowMatches[0];
  const lastRow = rowMatches[rowMatches.length - 1];
  const replacedRowsTable = `${tableXml.slice(0, firstRow.index || 0)}${rows.join('')}${tableXml.slice((lastRow.index || 0) + lastRow[0].length)}`;
  const nextTable = transformTable ? transformTable(replacedRowsTable) : replacedRowsTable;
  return `${xml.slice(0, match.index || 0)}${nextTable}${xml.slice((match.index || 0) + tableXml.length)}`;
}

function formatLongDate(value?: string) {
  const date = value ? new Date(value) : new Date();
  const safe = Number.isNaN(date.getTime()) ? new Date() : date;
  return safe.toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' });
}

function campaignPeriod(input: UsecurePhishingCampaignReportInput) {
  return `${formatLongDate(input.startDate)} au ${formatLongDate(input.endDate || input.startDate)}`;
}

function riskInteractionCount(input: UsecurePhishingCampaignReportInput) {
  const nominativeInteractions = input.stats.riskyUsers.filter((user) => !isGenericPhishingUser(user)).length;
  return nominativeInteractions || input.stats.clicks || input.stats.credentialSubmissions || 0;
}

function buildPhishingConclusion(input: UsecurePhishingCampaignReportInput) {
  const interactions = riskInteractionCount(input);
  const client = input.clientName || 'l’organisation';
  const subject = input.emailSubject || input.emailScenario || 'la campagne simulée';
  return `Les résultats démontrent que ${interactions} employés de ${client} ont interagi avec le courriel simulé « ${subject} ». Cette campagne met en évidence les comportements à risque à prioriser dans les actions de sensibilisation, notamment l’ouverture du message, le clic sur le lien et la saisie d’identifiants lorsque ces événements sont présents.`;
}

function buildAnnexeASenderParagraph(input: UsecurePhishingCampaignReportInput) {
  const senderName = input.senderName || 'l’expéditeur simulé';
  const senderEmail = input.senderEmail || 'l’adresse de l’expéditeur simulé';
  return `1 - Adresse de l’expéditeur : Il est très important de toujours vérifier l’expéditeur d’un courriel. Dans cette campagne, le courriel semblait provenir de « ${senderName} », mais l’adresse utilisée était « ${senderEmail} ». Cette information doit être comparée au domaine habituel de l’organisation ou du fournisseur légitime avant de faire confiance au message.`;
}

function buildAnnexeALinkParagraph(input: UsecurePhishingCampaignReportInput) {
  const url = input.landingPageUrl || 'l’URL de destination de la campagne';
  return `2 - Adresse du lien : Avant de cliquer sur un lien provenant d’un courriel, il est préférable de positionner le curseur sur le lien ou de vérifier l’adresse complète. Dans cette campagne, le lien pointait vers « ${url} », ce qui devait être validé avant toute saisie d’identifiants.`;
}

function buildAnnexeAScenarioParagraph(input: UsecurePhishingCampaignReportInput) {
  const scenario = input.emailScenario || input.emailSubject || 'le scénario utilisé dans la campagne';
  return `3 - Prétexte du message : Le scénario utilisé était « ${scenario} ». Les messages qui créent un sentiment d’urgence ou demandent une action rapide doivent être vérifiés par un canal officiel avant de cliquer ou de transmettre de l’information.`;
}

function resultFlags(action: string) {
  const normalized = normalize(action);
  return {
    opened: /ouvert|open|clic|click|visit|soumission|identifiant|comprom/.test(normalized) ? 'Oui' : 'Non',
    clicked: /clic|click|visit|lien/.test(normalized) ? 'Oui' : 'Non',
    submitted: /soumission|identifiant|credential|comprom/.test(normalized) ? 'Oui' : 'Non',
    reported: /signal|report/.test(normalized) ? 'Oui' : 'Non',
  };
}

function splitName(fullName: string) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return [parts[0] || '—', '—'];
  return [parts[0], parts.slice(1).join(' ')];
}

function isGenericPhishingUser(user: { name?: string; email?: string }) {
  const name = normalize(user.name || '');
  if (!name) return true;
  return /^utilisateur(?:\s+|$|\d)/.test(name) || name === 'utilisateur usecure' || name === 'utilisateur a risque';
}

function hasReportedAction(user: { action?: string }) {
  return resultFlags(user.action || '').reported === 'Oui';
}

function shouldIncludeReportedColumn(input: UsecurePhishingCampaignReportInput) {
  return Boolean(input.stats.reports && input.stats.reports > 0) || input.stats.riskyUsers.some(hasReportedAction);
}

function removeRowCellAt(rowXml: string, columnIndex: number) {
  const cells = splitRowCells(rowXml);
  const target = cells[columnIndex];
  return target ? rowXml.replace(target, '') : rowXml;
}

function removeTableColumnFromRows(rows: string[], columnIndex: number) {
  return rows.map((row) => removeRowCellAt(row, columnIndex));
}

function removeTableGridColumn(tableXml: string, columnIndex: number) {
  return tableXml.replace(/<w:tblGrid>[\s\S]*?<\/w:tblGrid>/, (gridXml) => {
    const gridCols = [...gridXml.matchAll(/<w:gridCol\b[^>]*\/>/g)].map((match) => match[0]);
    const target = gridCols[columnIndex];
    return target ? gridXml.replace(target, '') : gridXml;
  });
}

function removeRightBorderFromBorderContainers(tableXml: string) {
  return tableXml.replace(/<w:(?:tblBorders|tcBorders)>[\s\S]*?<\/w:(?:tblBorders|tcBorders)>/g, (bordersXml) => bordersXml.replace(/<w:right\b[^>]*\/>/g, ''));
}

export function buildEmployeeResultsTableRowsFromTemplate(tableXml: string, input: UsecurePhishingCampaignReportInput) {
  const rows = splitTableRows(tableXml);
  if (rows.length < 2) return rows;
  const headerRow = rows[0];
  const dataTemplate = rows.find((row, index) => index > 0 && splitRowCells(row).length >= 6) || rows[rows.length - 1];
  const includeReportedColumn = shouldIncludeReportedColumn(input);
  const users = input.stats.riskyUsers.filter((user) => !isGenericPhishingUser(user));
  const dataRows = users.length
    ? users.slice(0, 120).map((user) => {
      const [firstName, lastName] = splitName(user.name || '');
      const flags = resultFlags(user.action);
      const values = [firstName, lastName, flags.opened, flags.clicked, flags.submitted, flags.reported];
      return replaceRowCellTexts(dataTemplate, includeReportedColumn ? values : values.slice(0, 5));
    })
    : [replaceRowCellTexts(dataTemplate, includeReportedColumn ? ['Aucun utilisateur nominatif', '—', '—', '—', '—', '—'] : ['Aucun utilisateur nominatif', '—', '—', '—', '—'])];
  const nextRows = [headerRow, ...dataRows];
  return includeReportedColumn ? nextRows : removeTableColumnFromRows(nextRows, 5);
}

export function fillEmployeeResultsTable(xml: string, input: UsecurePhishingCampaignReportInput) {
  const tables = [...xml.matchAll(/<w:tbl(?=[\s>])[\s\S]*?<\/w:tbl>/g)];
  const annexeTableIndex = tables.length > 1 ? 1 : 0;
  const tableXml = tables[annexeTableIndex]?.[0];
  if (!tableXml) return xml;
  const includeReportedColumn = shouldIncludeReportedColumn(input);
  return replaceTableRowsByIndex(
    xml,
    annexeTableIndex,
    buildEmployeeResultsTableRowsFromTemplate(tableXml, input),
    includeReportedColumn ? undefined : (nextTableXml) => removeRightBorderFromBorderContainers(removeTableGridColumn(nextTableXml, 5)),
  );
}

export function fillPhishingCampaignTemplateXml(xml: string, input: UsecurePhishingCampaignReportInput) {
  let next = replacePhishingTemplateText(xml, input);
  next = replaceInFirstTable(next, formatMonthYear(input.endDate || input.startDate), formatCoverMonthYear(input.endDate || input.startDate));
  next = replaceParagraphContaining(next, 'Cher /', `Chers membres de l'équipe de ${input.clientName || 'Client'},`);
  next = replaceInlineTextAcrossRuns(next, {
    '[Nom du client]': input.clientName || 'Client',
    '[Mois Année]': formatMonthYear(input.endDate || input.startDate),
    'Mois Année': formatMonthYear(input.endDate || input.startDate),
  });
  next = replaceParagraphContaining(next, 'possédant une adresse courriel', `La campagne d’hameçonnage « ${input.campaignName || 'campagne uSecure'} » réalisée du ${campaignPeriod(input)} concernait les employés ciblés de ${input.clientName || 'l’organisation'}.`);
  next = replaceParagraphContaining(next, 'Le courriel envoyé simulait une demande', `Le courriel envoyé simulait le scénario « ${input.emailScenario || input.emailSubject || 'hameçonnage'} », avec l’expéditeur « ${input.senderName || '—'} » <${input.senderEmail || '—'}> et le sujet « ${input.emailSubject || '—'} ».`);
  next = replaceParagraphContaining(next, 'Ajouter capture d’écran du courriel', input.emailScreenshotDataUrl ? '__PRISME_EMAIL_SCREENSHOT__' : 'Image du courriel envoyé à insérer avant la figure 1.');
  if (!input.emailContainsQrCode) {
    next = replaceInlineTextAcrossRuns(next, { 'Cliquer sur un lien ou balayer un code QR': 'Cliquer sur un lien' });
  }
  next = replaceParagraphContaining(next, 'Voici les résultats de la campagne', `Voici les résultats de la campagne d’hameçonnage effectuée en ${formatMonthYear(input.endDate || input.startDate)} :`);
  next = replaceParagraphContaining(next, 'Ajouter le graphique', '__PRISME_RESULTS_CHART__');
  next = replaceParagraphContaining(next, 'Un total de 53 courriels', `Un total de ${formatMetric(input.stats.emailsSent || input.stats.targetedUsers)} courriels a été envoyé aux employés de ${input.clientName || 'l’organisation'}. ${formatMetric(input.stats.emailsOpened)} ont été ouverts, ${formatMetric(input.stats.clicks)} ont mené à un clic, ${formatMetric(input.stats.credentialSubmissions)} ont mené à une soumission d’identifiants et ${formatMetric(input.stats.reports)} ont été signalés.`);
  next = replaceParagraphContaining(next, 'Les résultats démontrent qu’un certain nombre', buildPhishingConclusion(input));
  next = replaceParagraphContaining(next, 'Bien que ce courriel semblait provenir', buildAnnexeASenderParagraph(input));
  next = replaceParagraphContaining(next, 'Dans la composition d’une adresse courriel', `Le domaine de l’adresse « ${input.senderEmail || '—'} » doit être comparé au domaine attendu. Un domaine inhabituel, une faute ou un sous-domaine trompeur constitue un signal d’alerte.`);
  next = replaceParagraphContaining(next, 'Dans ce cas-ci, le lien pointait vers', buildAnnexeALinkParagraph(input));
  next = replaceParagraphContaining(next, 'Les courriels envoyés lors de cette campagne', buildAnnexeAScenarioParagraph(input));
  next = replaceParagraphContaining(next, 'la page de connexion ne correspondait pas', `Pour la présente campagne d’hameçonnage, la page de destination était « ${input.landingPageUrl || '—'} ». Les utilisateurs devaient valider l’adresse et l’apparence de cette page avant toute saisie d’identifiants.`);
  next = ensurePageBreakBeforeExactParagraph(next, 'Recommandations');
  next = fillEmployeeResultsTable(next, input);
  return next;
}

async function loadPhishingReportTemplate(templateUrl = PHISHING_CAMPAIGN_REPORT_TEMPLATE_URL) {
  const response = await fetch(templateUrl, { credentials: 'include' });
  if (!response.ok) throw new Error('Impossible de charger le gabarit Word du rapport d’hameçonnage.');
  return response.arrayBuffer();
}


export function fillPhishingCampaignTemplateDocx(zip: PizZip, input: UsecurePhishingCampaignReportInput) {
  const image = decodeDataUrl(input.emailScreenshotDataUrl);
  let screenshotRelId = '';
  let resultsChartRelId = '';
  const relsPath = 'word/_rels/document.xml.rels';
  const relsFile = zip.file(relsPath);
  const contentTypesFile = zip.file('[Content_Types].xml');

  if (image) {
    const mediaPath = `word/media/prisme-email-screenshot.${image.extension}`;
    zip.file(mediaPath, image.base64, { base64: true, binary: true });
    if (relsFile) {
      const relsXml = zip.file(relsPath)?.asText() || relsFile.asText();
      screenshotRelId = nextRelationshipId(relsXml);
      zip.file(relsPath, relsXml.replace('</Relationships>', `<Relationship Id="${screenshotRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/prisme-email-screenshot.${image.extension}"/></Relationships>`));
    }
    if (contentTypesFile) {
      const contentType = image.extension === 'jpg' ? 'image/jpeg' : image.extension === 'gif' ? 'image/gif' : 'image/png';
      zip.file('[Content_Types].xml', ensureContentTypeDefault(zip.file('[Content_Types].xml')?.asText() || contentTypesFile.asText(), image.extension, contentType));
    }
  }

  const resultsChartSvg = buildPhishingResultsChartSvg(input);
  zip.file('word/media/prisme-results-chart.svg', resultsChartSvg);
  if (relsFile) {
    const relsXml = zip.file(relsPath)?.asText() || relsFile.asText();
    resultsChartRelId = nextRelationshipId(relsXml);
    zip.file(relsPath, relsXml.replace('</Relationships>', `<Relationship Id="${resultsChartRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/prisme-results-chart.svg"/></Relationships>`));
  }
  if (contentTypesFile) {
    zip.file('[Content_Types].xml', ensureContentTypeDefault(zip.file('[Content_Types].xml')?.asText() || contentTypesFile.asText(), 'svg', 'image/svg+xml'));
  }

  zip.file(/word\/.*\.xml/).forEach((file: { name: string; asText: () => string }) => {
    let xml = file.name === 'word/document.xml'
      ? fillPhishingCampaignTemplateXml(file.asText(), input)
      : replacePhishingTemplateText(file.asText(), input);
    if (file.name === 'word/document.xml' && screenshotRelId) {
      xml = xml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => paragraph.includes('__PRISME_EMAIL_SCREENSHOT__') ? imageDrawingParagraph(screenshotRelId, input.emailScreenshotName || 'courriel.png', { description: 'Capture d’écran du courriel envoyé' }) : paragraph);
      if (!xml.includes(screenshotRelId)) {
        xml = xml.replace(/(<w:p(?=[\s>])[\s\S]*?Figure 1 - Contenu du courriel[\s\S]*?<\/w:p>)/, `${imageDrawingParagraph(screenshotRelId, input.emailScreenshotName || 'courriel.png', { description: 'Capture d’écran du courriel envoyé' })}$1`);
      }
    }
    if (file.name === 'word/document.xml' && resultsChartRelId) {
      xml = xml.replace(/<w:p(?=[\s>])[\s\S]*?<\/w:p>/g, (paragraph) => paragraph.includes('__PRISME_RESULTS_CHART__') ? imageDrawingParagraph(resultsChartRelId, 'prisme-results-chart.svg', { docPrId: 92001, name: 'Figure 2 - Résultats de la campagne', description: 'Graphique des résultats de la campagne comparés à la moyenne des clients de BZ', cx: 6400800, cy: 3619500 }) : paragraph);
    }
    zip.file(file.name, xml);
  });

  zip.file(/docProps\/.*\.xml/).forEach((file: { name: string; asText: () => string }) => {
    zip.file(file.name, fillPhishingDocumentPropertiesXml(file.asText(), input));
  });
  return zip;
}

export async function generateUsecurePhishingCampaignDocx(input: UsecurePhishingCampaignReportInput): Promise<Blob> {
  const templateBuffer = await loadPhishingReportTemplate();
  const zip = new PizZip(templateBuffer);
  const documentFile = zip.file('word/document.xml');
  if (!documentFile) throw new Error('Structure DOCX invalide: word/document.xml absent');
  fillPhishingCampaignTemplateDocx(zip, input);
  return zip.generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
